import { useEffect, useRef, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { LiaTimesSolid } from "react-icons/lia";
import { BsChevronDown } from "react-icons/bs";
import { MdModeEdit } from "react-icons/md";
import truckOptions from "../TruckCleaningUtils/truck_options.json";
import axios from "../../../../Axios";
import styles from "./TruckSelector.module.css";
import { Urls } from "../../../../Urls";

export default function TruckSelector({
  truckType,
  setTruckType,
  width = 200,
  fontSize = "13px",
  menuHeight = 220,
  className = "",
  inlineDimensions = false,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef(null);

  const handleCreateOption = async (inputValue) => {
    setTruckType({
      raw_truck_name: inputValue,
      status: "loading",
    });

    setIsLoading(true);
    const cleanTruckUrl = `${Urls.rpt_base_url}/clean_truck`;

    try {
      const { data } = await axios.post(cleanTruckUrl, {
        raw_truck_name: inputValue,
      });

      const cleanedDims = data.dimensions;

      setTruckType({
        raw_truck_name: inputValue,
        cleaned_code: data.cleaned_code,
        cleaned_dimensions: {
          dimensions: { ...cleanedDims },
        },
        user_dimensions: { ...cleanedDims },
        status: "inferred",
      });
    } catch (err) {
      console.error("Failed to fetch truck dimensions:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setTruckType({
      raw_truck_name: "",
      cleaned_code: "",
      cleaned_dimensions: {
        dimensions: {
          capacity_mt: "",
          length_ft: "",
          axle_type: "",
          body_type: "",
        },
      },
      user_dimensions: {
        capacity_mt: "",
        length_ft: "",
        axle_type: "",
        body_type: "",
      },
      status: "non_inferred",
    });
  };

  const handleDimensionChange = (field, value) => {
    setTruckType({
      ...truckType,
      user_dimensions: {
        ...truckType.user_dimensions,
        [field]: value,
      },
      status: truckType.status === "inferred" ? "edited" : truckType.status,
    });
  };

  const axleTypeOptions = [
    { label: "Single Axle", value: "SA" },
    { label: "Multi Axle", value: "MA" },
  ];

  const bodyTypeOptions = [
    { label: "Closed Body", value: "CB" },
    { label: "Open Body", value: "OB" },
    { label: "Reefer", value: "RF" },
    { label: "Container", value: "CB" },
    { label: "Flatbed", value: "OB" },
    { label: "Tanker", value: "CB" },
  ];

  const hasMissingDimensions =
    inlineDimensions &&
    truckType?.raw_truck_name &&
    (!truckType?.user_dimensions?.capacity_mt ||
      !truckType?.user_dimensions?.length_ft ||
      !truckType?.user_dimensions?.axle_type ||
      !truckType?.user_dimensions?.body_type);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setShowPopover(false);
      }
    };
    if (showPopover) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopover]);

  return (
    <div className={`${styles.selectorWrapper} ${className}`} style={{ width }}>
      <div style={{ flex: 1 }}>
        <CreatableSelect
          options={truckOptions}
          isLoading={isLoading}
          value={
            truckType?.raw_truck_name
              ? {
                  label: truckType.raw_truck_name,
                  value: truckType.raw_truck_name,
                }
              : null
          }
          onChange={(option) => {
            setTruckType({
              raw_truck_name: option?.label,
              cleaned_code: option?.truck_code,
              cleaned_dimensions: {
                dimensions: { ...option?.dimensions },
              },
              user_dimensions: { ...option?.dimensions },
              status: "non_inferred",
            });
          }}
          onCreateOption={handleCreateOption}
          classNamePrefix="select"
          components={{
            IndicatorSeparator: () => null,
            DropdownIndicator: () => (
              <BsChevronDown className="text-muted mx-3" />
            ),
            ClearIndicator: () => (
              <LiaTimesSolid className="text-muted" onClick={handleClear} />
            ),
          }}
          styles={{
            control: (base) => ({
              ...base,
              fontSize,
              cursor: "pointer",
              minHeight: "32px",
            }),
            menu: (base) => ({
              ...base,
              fontSize,
            }),
            menuList: (base) => ({
              ...base,
              maxHeight: menuHeight,
              overflowY: "auto",
            }),
          }}
          isClearable={true}
        />
        {inlineDimensions && hasMissingDimensions && (
          <div className={styles.errorMessage}>Some dimensions are missing</div>
        )}
      </div>

      {inlineDimensions && truckType?.raw_truck_name && (
        <div
          style={{ position: "relative", display: "inline-block" }}
          className={hasMissingDimensions ? styles.errorEditButton : ""}
        >
          <button
            className={`${styles.editButton}`}
            onClick={() => setShowPopover((prev) => !prev)}
            title={"Edit truck dimensions"}
          >
            <MdModeEdit />
          </button>
        </div>
      )}

      {showPopover && (
        <div className={styles.popover} ref={popoverRef}>
          <div className={styles.popoverField}>
            <label>Capacity (MT)</label>
            <input
              value={truckType?.user_dimensions?.capacity_mt || ""}
              onChange={(e) =>
                handleDimensionChange("capacity_mt", e.target.value)
              }
            />
          </div>
          <div className={styles.popoverField}>
            <label>Length (FT)</label>
            <input
              value={truckType?.user_dimensions?.length_ft || ""}
              onChange={(e) =>
                handleDimensionChange("length_ft", e.target.value)
              }
            />
          </div>
          <div className={styles.popoverField}>
            <label>Axle Type</label>
            <select
              value={truckType?.user_dimensions?.axle_type || ""}
              onChange={(e) =>
                handleDimensionChange("axle_type", e.target.value)
              }
            >
              <option value="">-- Select --</option>
              {axleTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.popoverField}>
            <label>Body Type</label>
            <select
              value={truckType?.user_dimensions?.body_type || ""}
              onChange={(e) =>
                handleDimensionChange("body_type", e.target.value)
              }
            >
              <option value="">-- Select --</option>
              {bodyTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
